import { get } from 'lodash';
import getAnalyticsSectionTitle from './getAnalyticsSectionTitle';
import getGTMProductListName from './getGTMProductListName';
import groupBy from 'utils/groupBy';
import encodeName from './encodeName';
import SectionType from 'apps/catalogue/models/SectionType';
import ProductLabel from 'apps/products/models/ProductLabel';

const MAX_IMPRESSIONS_PER_EVENT = 30;

const buildSingleImpressionsEvent = ({
  pageType,
  sectionTitle,
  impressionFieldObjects,
  numberOfResults,
  sortBy,
  filterValues,
}) => {
  const universalAnalyticsEventData = {
    uaEventCategory: 'E-Commerce',
    uaEventAction: 'productImpressions',
    uaEventLabel: `${pageType}|${sectionTitle}`,
    uaEventValue: impressionFieldObjects.length,
  };

  const customDimension = {
    pageTypeDimension: pageType,
    productSectionDimension: sectionTitle,
  };

  return {
    event: 'eecProductInteractionEvent',
    filterValues,
    numberOfResults,
    sortBy,
    ecommerce: {
      currencyCode: 'GBP',
      impressions: impressionFieldObjects,
    },
    ...customDimension,
    ...universalAnalyticsEventData,
  };
};

const buildImpressionsEvents = ({ impressionsBySection, pageType, numberOfResults, sortBy, filterValues }) => {
  const impressionsEvents = [];

  Object.keys(impressionsBySection).forEach(sectionPropName => {
    const impressionsForCurrentSection = impressionsBySection[sectionPropName];

    for (let i = 0; i < impressionsForCurrentSection.length; i += MAX_IMPRESSIONS_PER_EVENT) {
      const blockOfImpressions = impressionsForCurrentSection.slice(i, i + MAX_IMPRESSIONS_PER_EVENT);

      impressionsEvents.push(
        buildSingleImpressionsEvent({
          pageType,
          sectionTitle: sectionPropName,
          impressionFieldObjects: blockOfImpressions,
          numberOfResults,
          sortBy,
          filterValues,
        })
      );
    }
  });

  return impressionsEvents;
};

const createImpressionFieldObject = ({
  gaMinProductImpressions,
  listName,
  product,
  collectionOrderDetails,
  sectionType,
}) => {
  const minImpressionFieldObject = {
    id: String(product.sku),
    price: product.price.current,
    position: collectionOrderDetails.fopInCollection,
    list: listName,
  };
  const productLabels = get(product, 'labels', []);
  const productRating = get(product, 'reviewStats.averageRate', 0);
  const productOffer = get(product, 'offer', {});

  if (gaMinProductImpressions) {
    return minImpressionFieldObject;
  }

  return {
    ...minImpressionFieldObject,
    name: encodeName(product.name),
    brand: (product.brand || {}).name,
    category: product.mainCategory,
    dimension60: productRating > 0 ? productRating.toFixed(1) : undefined, // product rating
    dimension70: get(product, 'coupon.id'), // product coupon
    canAddToBasket: !productLabels.includes(ProductLabel.OOS),
    isFavorite: productLabels.includes(ProductLabel.FAVOURITE),
    isFeatured: sectionType === SectionType.FEATURED,
    isOnPromotion: productLabels.includes(ProductLabel.ON_OFFER),
    promotionIds: get(productOffer, 'id'),
    promotionNames: get(productOffer, 'text'),
  };
};

const createImpressionsObject = (
  { productImpressionData, pageType, useSectionTitleAsListName, gaMinProductImpressions }
) => {
  const { section, product, collectionOrderDetails } = productImpressionData;
  const sectionTitle = getAnalyticsSectionTitle(section, pageType);
  const sectionType = get(section, 'attributes.type');
  const listName = useSectionTitleAsListName ? sectionTitle : getGTMProductListName(pageType, section);
  const impressionFieldObject = createImpressionFieldObject({
    gaMinProductImpressions,
    listName,
    product,
    collectionOrderDetails,
    sectionType,
  });
  return { sectionTitle, impressionFieldObject };
};

export default function buildProductImpressionsEvents(eventData, gaMinProductImpressions) {
  const {
    pageType,
    numberOfResults,
    sortBy,
    filterValues,
    productImpressionsData,
    useSectionTitleAsListName,
  } = eventData;

  const impressionsObjects = productImpressionsData.map(productImpressionData =>
    createImpressionsObject({ productImpressionData, pageType, useSectionTitleAsListName, gaMinProductImpressions }),
  );

  const impressionsBySection = groupBy(
    impressionsObjects, 'sectionTitle', impression => impression.impressionFieldObject
  );

  return buildImpressionsEvents({ impressionsBySection, pageType, numberOfResults, sortBy, filterValues });
}
